import { useState } from 'react'
import { useTableColumns } from '@/hooks/useTableColumns'
import { useSortedRows } from '@/hooks/useSortedRows'
import type { TableColumnDef } from '@/lib/tableColumnPrefs'
import type { SortAccessors, SortValue } from '@/lib/tableSort'
import { ColumnCells, ResizableTh } from '@/components/table'
import { useNavigate } from 'react-router-dom'
import { Download, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import type { FormSubmission } from '@/types/cgt'
import { skippedMappingsMessage } from '@/lib/skippedMappings'
import {
  formPopulationApi,
  type FormValueProposal,
  type SkippedMapping,
} from '@/api/formPopulationApi'

/**
 * Self-contained "populate this form from instrument results" action. Reviews
 * proposed values and, on confirm, writes them via the form-population API (which
 * audits), then hands the updated submission back to the parent. Mounted with a
 * single line on the form/procedure page; no coupling to the forms renderer internals.
 */
// Per-user column layout. The tick column and Field are locked: without the
// tick nothing can be selected to apply, and without Field a row does not say
// what it would write. See src/components/table/index.ts for the pattern.
const POPULATE_TABLE_ID = 'instrument-proposals'

const POPULATE_COLUMNS: TableColumnDef[] = [
  { id: 'select', header: 'Select', locked: true },
  { id: 'field', header: 'Field', locked: true, sortable: true },
  { id: 'current', header: 'Current', sortable: true },
  { id: 'proposed', header: 'Proposed', sortable: true },
  { id: 'change', header: 'Change', sortable: true },
  { id: 'source', header: 'Source', sortable: true },
]

/** A proposed/current value as itself when it is a scalar, so 10 sorts after 9. */
function proposalValue(v: unknown): SortValue {
  if (v === null || v === undefined) return null
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v
  return String(v)
}

// Column sorting (z8nrz7e4h6). The tick column holds only a control.
const POPULATE_SORT: SortAccessors<FormValueProposal> = {
  field: (p) => p.fieldLabel,
  current: (p) => proposalValue(p.currentValue),
  proposed: (p) => proposalValue(p.proposedValue),
  change: (p) => p.status,
  source: (p) => p.source,
}

export function PopulateFromInstrument({
  submission,
  onSubmissionUpdated,
}: {
  submission: FormSubmission
  onSubmissionUpdated: (updated: FormSubmission) => void
}) {
  const cols = useTableColumns(POPULATE_TABLE_ID, POPULATE_COLUMNS)
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState<string | undefined>(undefined)
  const [matchInfo, setMatchInfo] = useState<{ want?: string; have?: string }>({})
  const [proposals, setProposals] = useState<FormValueProposal[]>([])
  // Mappings whose target is gone from the form. Shown in every state, because
  // they are dropped whether or not other mappings produced proposals.
  const [skipped, setSkipped] = useState<SkippedMapping[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [applied, setApplied] = useState<number | null>(null)
  const sortedProposals = useSortedRows(proposals, cols.sort, POPULATE_SORT)

  const keyOf = (p: FormValueProposal) => `${p.resultId}:${p.mappingId}`

  function applyResult(res: Awaited<ReturnType<typeof formPopulationApi.proposals>>) {
    setProposals(res.proposals)
    setReason(res.reason)
    setMatchInfo({ want: res.want, have: res.have })
    setSkipped(res.skipped ?? [])
    setSelected(new Set(res.proposals.filter((p) => p.status !== 'unchanged').map(keyOf)))
  }

  async function loadProposals(autoCorrelate = true) {
    setLoading(true)
    setError(null)
    setApplied(null)
    try {
      const res = await formPopulationApi.proposals(submission.id)
      // The procedure isn't linked to a sample yet — link it to the demo sample and retry
      // once, so the dialog populates without a manual correlate step. Mappings are
      // authored per-integration (Integrations → integration → Mappings tab).
      if (res.reason === 'no-correlation' && autoCorrelate) {
        await formPopulationApi.correlate(submission.id)
        applyResult(await formPopulationApi.proposals(submission.id))
      } else {
        applyResult(res)
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load proposals.')
    } finally {
      setLoading(false)
    }
  }

  function openModal() {
    setOpen(true)
    loadProposals()
  }

  async function apply() {
    const accepted = proposals
      .filter((p) => selected.has(keyOf(p)))
      .map((p) => ({ resultId: p.resultId, mappingId: p.mappingId }))
    if (accepted.length === 0) return
    setBusy(true)
    setError(null)
    try {
      const res = await formPopulationApi.apply(submission.id, accepted)
      setApplied(res.applied)
      if (res.submission) onSubmissionUpdated(res.submission as FormSubmission)
      await loadProposals()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to apply values.')
    } finally {
      setBusy(false)
    }
  }

  const toggle = (k: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })

  const fmt = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v))

  return (
    <>
      <Button variant="outline" size="sm" onClick={openModal}>
        <Download className="h-3.5 w-3.5 mr-1.5" />
        Populate from instrument
      </Button>

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Populate from instrument</DialogTitle>
            <DialogDescription>
              Review instrument values before they are written to this form. Nothing is saved
              until you click Apply.
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {!loading && skipped.length > 0 && (
            <div className="flex items-start gap-2 text-sm text-amber-700 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                {skippedMappingsMessage(skipped)}{' '}
                Fix this on the integration’s <strong>Mappings</strong> tab.
              </span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : proposals.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground space-y-3">
              {reason === 'no-results' ? (
                <p>No instrument results for this procedure yet — run <strong>Fetch</strong> on the integration first.</p>
              ) : reason === 'no-mappings' ? (
                <>
                  <p>No instrument mappings for this form yet.</p>
                  <div className="flex items-center justify-center">
                    <Button size="sm" variant="outline" onClick={() => navigate('/integrations')}>
                      Set up on an integration
                    </Button>
                  </div>
                  <p className="text-xs">Open an integration and use its <strong>Mappings</strong> tab.</p>
                </>
              ) : reason === 'no-correlation' ? (
                <p>This procedure has no patient or sample identifiers to link to instrument data.</p>
              ) : reason === 'no-match' ? (
                <p>
                  Results are stored, but none match this form’s fields. This form maps to{' '}
                  <strong>{matchInfo.want || 'instrument'}</strong> values, but the latest fetched
                  results are <strong>{matchInfo.have || 'a different instrument'}</strong>. Fetch
                  the matching instrument (with the same DIN / product code), then retry.
                </p>
              ) : (
                <p>No proposed values.</p>
              )}
            </div>
          ) : (
            <>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-table-header border-b">
                  <tr>
                    {cols.visible.map(({ def }) => (
                      <ResizableTh
                        key={def.id}
                        columnId={def.id}
                        columns={cols}
                        label={def.header}
                        className={
                          def.id === 'select'
                            ? 'w-8 px-3 py-2'
                            : 'text-left px-3 py-2 font-medium text-table-header-foreground'
                        }
                        resizable={def.id !== 'select'}
                      >
                        {def.id === 'select' ? null : def.header}
                      </ResizableTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedProposals.map((p) => {
                    const k = keyOf(p)
                    const unchanged = p.status === 'unchanged'
                    return (
                      <tr key={k} className={unchanged ? 'opacity-60' : ''}>
                        <ColumnCells
                          columns={cols}
                          cells={{
                            select: (
                              <td className="px-3 py-2">
                                <Checkbox
                                  checked={selected.has(k)}
                                  disabled={unchanged}
                                  onCheckedChange={() => toggle(k)}
                                />
                              </td>
                            ),
                            field: <td className="px-3 py-2 font-medium">{p.fieldLabel}</td>,
                            current: <td className="px-3 py-2 text-muted-foreground">{fmt(p.currentValue)}</td>,
                            proposed: <td className="px-3 py-2">{fmt(p.proposedValue)}</td>,
                            change: (
                              <td className="px-3 py-2">
                                <StatusChip status={p.status} />
                              </td>
                            ),
                            source: <td className="px-3 py-2 text-xs text-muted-foreground">{p.source}</td>,
                          }}
                        />
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          {applied !== null && (
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle2 className="h-4 w-4" /> Wrote {applied} value(s) to the form.
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>
              Close
            </Button>
            <Button onClick={apply} disabled={busy || loading || selected.size === 0}>
              {busy && <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />}
              Apply selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

/** New / changed / unchanged marker for a proposed value. */
function StatusChip({ status }: { status: FormValueProposal['status'] }) {
  const styles: Record<FormValueProposal['status'], string> = {
    new: 'border-green-200 bg-green-100 text-green-700',
    changed: 'border-amber-200 bg-amber-100 text-amber-700',
    unchanged: 'border-gray-200 bg-gray-100 text-gray-500',
  }
  return (
    <span className={`rounded border px-1.5 py-0.5 text-[10px] ${styles[status]}`}>{status}</span>
  )
}
